import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import { Member } from '../../../../../../types/entity.types';
import { RadioOrSelectOption } from '../../../../../../types/common.types';

import Modal from '../../../../../common/modal/Modal';
import Dropdown from '../../../../../common/input/dropdown/Dropdown';
import Button from '../../../../../common/button/Button';

import '../team-modals.scss';

type Props = {
  show: boolean;
  close: () => void;
  members: Member[];
  addMemberToTeam: (memberExternalReference: string, close: () => void) => void;
};

const AddTeamMemberModal = ({ show, close, members, addMemberToTeam }: Props): ReactElement => {
  const [selectedMemberOption, setSelectedMemberOption] = useState<RadioOrSelectOption | undefined>(undefined);

  useEffect((): void => {
    if (!show) {
      setSelectedMemberOption(undefined);
    }
  }, [show]);

  const sortedMemberOptions: RadioOrSelectOption[] = useMemo((): RadioOrSelectOption[] => {
    const sortedMembers: Member[] = [...members].sort((memberOne: Member, memberTwo: Member): number => {
      if (!memberOne.name || !memberTwo.name) return 0;
      if (memberOne.name > memberTwo.name) return 1;
      if (memberOne.name < memberTwo.name) return -1;
      return 0;
    });

    return sortedMembers.map(
      (member: Member): RadioOrSelectOption => ({
        label: member.name!,
        value: member.memberExternalReference!
      })
    );
  }, [members]);

  const handleButtonClick = (): void => {
    if (!selectedMemberOption) return;
    addMemberToTeam(selectedMemberOption.value, close);
  };

  if (!show) return <React.Fragment />;

  return (
    <Modal show={show}>
      <section className="team-modals">
        <h1 className="team-modals__header">Add Member</h1>
        <section className="add-team-member-modal__member-dropdown-container">
          <label className="add-team-member-modal__member-dropdown-label" htmlFor="members">
            Member:
          </label>
          <div className="add-team-member-modal__member-dropdown">
            <Dropdown
              name="members"
              value={selectedMemberOption}
              options={sortedMemberOptions}
              onChange={setSelectedMemberOption}
            />
          </div>
        </section>
        <section className="team-modals__button-container">
          <Button text="Cancel" onClick={close} />
          <Button text="Save" onClick={handleButtonClick} disabled={!selectedMemberOption} />
        </section>
      </section>
    </Modal>
  );
};

export default AddTeamMemberModal;
